import config from "../../Configurations";

const PREVIEW_VALUES = {
  brand: "Pivony",
  customer_name: "Alex",
  order_id: "ORD-4821",
  discount_code: "SAVE10",
  product_name: "Sneakers",
  agent_name: "Sonic",
};

export const fillGreetingForPreview = (text, brand = PREVIEW_VALUES.brand) =>
  (text || "")
    .replace(/\{\{brand\}\}/gi, brand)
    .replace(/\{\{(\w+)\}\}/g, (_, key) => PREVIEW_VALUES[key] || key)
    .trim();

export const isSpeechPreviewSupported = () =>
  typeof window !== "undefined" &&
  (typeof window.Audio !== "undefined" || "speechSynthesis" in window);

let activeAudio = null;
let activeObjectUrl = null;
let activeWebAudioSource = null;
let sharedAudioContext = null;

const getAudioContext = () => {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!sharedAudioContext) sharedAudioContext = new Ctx();
  return sharedAudioContext;
};

/** Call synchronously from a click handler so async TTS can play afterward. */
export const unlockAudioPlayback = () => {
  const ctx = getAudioContext();
  if (ctx?.state === "suspended") {
    ctx.resume().catch(() => {});
  }
};

const cleanupActiveAudio = () => {
  if (activeWebAudioSource) {
    try {
      activeWebAudioSource.stop();
    } catch (_) {
      // already stopped
    }
    activeWebAudioSource = null;
  }
  if (activeAudio) {
    activeAudio.pause();
    activeAudio = null;
  }
  if (activeObjectUrl) {
    URL.revokeObjectURL(activeObjectUrl);
    activeObjectUrl = null;
  }
  if (typeof window !== "undefined" && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
};

export const stopSpeechPreview = () => {
  cleanupActiveAudio();
};

export const isSpeechPreviewSpeaking = () => {
  if (activeAudio && !activeAudio.paused && !activeAudio.ended) return true;
  if (activeWebAudioSource) return true;
  return (
    typeof window !== "undefined" &&
    window.speechSynthesis &&
    window.speechSynthesis.speaking
  );
};

const authHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  const tokenStr = window.localStorage.getItem("jwt_token");
  if (!tokenStr) return headers;
  try {
    const item = JSON.parse(tokenStr);
    if (item?.value) headers.Authorization = `Bearer ${item.value}`;
  } catch (_) {
    // ignore
  }
  return headers;
};

const speechLang = (languageCode) => {
  const code = (languageCode || "en-US").trim();
  if (/^[a-z]{2}-[A-Z]{2}$/.test(code)) return code;
  const lower = code.toLowerCase();
  if (lower.startsWith("tr")) return "tr-TR";
  if (lower.startsWith("en")) return "en-US";
  if (lower.startsWith("de")) return "de-DE";
  if (lower.startsWith("ar")) return "ar-SA";
  return code;
};

const normalizeMp3Blob = (blob) => {
  if (!blob) return null;
  if (blob.type && blob.type.includes("audio")) return blob;
  return new Blob([blob], { type: "audio/mpeg" });
};

const playBlobWithWebAudio = async (blob, { onStart, onEnd }) => {
  const ctx = getAudioContext();
  if (!ctx) throw new Error("playback");
  await ctx.resume();
  const buffer = await blob.arrayBuffer();
  const audioBuffer = await ctx.decodeAudioData(buffer.slice(0));
  return new Promise((resolve, reject) => {
    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);
    activeWebAudioSource = source;
    source.onended = () => {
      activeWebAudioSource = null;
      if (onEnd) onEnd();
      resolve();
    };
    if (onStart) onStart();
    try {
      source.start(0);
    } catch (err) {
      activeWebAudioSource = null;
      reject(err);
    }
  });
};

const playBlob = async (blob, { onStart, onEnd, onError }) => {
  cleanupActiveAudio();
  const typedBlob = normalizeMp3Blob(blob);
  if (!typedBlob) throw new Error("playback");

  try {
    await playBlobWithWebAudio(typedBlob, { onStart, onEnd });
    return;
  } catch (_) {
    // Fall back to HTMLAudioElement (works in some browsers where decode fails).
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(typedBlob);
    activeObjectUrl = url;
    const audio = new Audio(url);
    activeAudio = audio;
    audio.onplay = () => {
      if (onStart) onStart();
    };
    audio.onended = () => {
      cleanupActiveAudio();
      if (onEnd) onEnd();
      resolve();
    };
    audio.onerror = () => {
      cleanupActiveAudio();
      reject(new Error("playback"));
    };
    audio.play().catch((err) => {
      cleanupActiveAudio();
      reject(err);
    });
  }).catch((err) => {
    if (onError) onError("playback");
    throw err;
  });
};

const fetchElevenLabsPreviewBlob = async (text, { languageCode, voiceId, voiceEngine }) => {
  const res = await fetch(`${config.BASE_URL}sonic/voice/preview-tts`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      text,
      language: languageCode,
      voice_engine: voiceEngine || undefined,
      elevenlabs_voice_id: voiceId || undefined,
    }),
  });
  if (!res.ok) {
    let message = "tts_error";
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch (_) {
      // ignore
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    throw new Error("tts_error");
  }
  return normalizeMp3Blob(blob);
};

const speakWithBrowserFallback = async (spoken, languageCode, handlers) => {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    if (handlers.onError) handlers.onError("unsupported");
    return;
  }
  const synth = window.speechSynthesis;
  const utterance = new SpeechSynthesisUtterance(spoken);
  utterance.lang = speechLang(languageCode);
  utterance.rate = 0.98;
  utterance.onstart = () => handlers.onStart && handlers.onStart();
  utterance.onend = () => handlers.onEnd && handlers.onEnd();
  utterance.onerror = () => handlers.onError && handlers.onError("playback");
  synth.speak(utterance);
};

const isPlaybackError = (err) => {
  const msg = String(err?.message || "");
  return msg === "playback" || err?.name === "NotAllowedError";
};

export const speakGreetingPreview = async (
  text,
  { languageCode, voiceId, voiceEngine, preferElevenLabs = true, onStart, onEnd, onError }
) => {
  const spoken = fillGreetingForPreview(text);
  if (!spoken) {
    if (onError) onError("empty");
    return;
  }

  stopSpeechPreview();

  if (preferElevenLabs) {
    try {
      const blob = await fetchElevenLabsPreviewBlob(spoken, {
        languageCode,
        voiceId,
        voiceEngine,
      });
      await playBlob(blob, { onStart, onEnd, onError });
      return;
    } catch (err) {
      if (isPlaybackError(err)) {
        return;
      }
      const msg = String(err?.message || "");
      if (msg.includes("not configured") || msg.includes("not enabled") || msg.includes("503")) {
        await speakWithBrowserFallback(spoken, languageCode, { onStart, onEnd, onError });
        return;
      }
      if (onError) onError("api");
      return;
    }
  }

  await speakWithBrowserFallback(spoken, languageCode, { onStart, onEnd, onError });
};

export const playPreviewUrl = async (url, { onStart, onEnd, onError } = {}) => {
  if (!url) return false;
  unlockAudioPlayback();
  stopSpeechPreview();
  try {
    const audio = new Audio(url);
    activeAudio = audio;
    audio.onplay = () => {
      if (onStart) onStart();
    };
    audio.onended = () => {
      cleanupActiveAudio();
      if (onEnd) onEnd();
    };
    audio.onerror = () => {
      cleanupActiveAudio();
      if (onError) onError("playback");
    };
    await audio.play();
    return true;
  } catch (_) {
    cleanupActiveAudio();
    if (onError) onError("playback");
    return false;
  }
};

export const playBase64Mp3 = async (base64, { onStart, onEnd, onError } = {}) => {
  if (!base64) return;
  unlockAudioPlayback();
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: "audio/mpeg" });
    await playBlob(blob, { onStart, onEnd, onError });
  } catch (err) {
    if (!isPlaybackError(err) && onError) onError("playback");
  }
};
